import { useAuth } from '../../../../hooks/useAuth'

/**
 * Single Source of Truth for Project Management RBAC permissions.
 *
 * Implements Option A (Prerequisite Dependency Hierarchy):
 * - ProjectFinancialsView is the foundational prerequisite.
 * - Customer 360 and AMC Billing Schedule sub-features strictly require
 *   canViewFinancials to be true before their individual privileges can take effect.
 */
export function useProjectPermissions() {
  const { hasPrivilege, isAdmin } = useAuth()

  // ── Core Project Operations ────────────────────────────────────────────────
  const canViewProject = Boolean(isAdmin || hasPrivilege('ProjectView'))
  const canEditProject = Boolean(isAdmin || hasPrivilege('ProjectEdit'))
  const canCreateProject = Boolean(isAdmin || hasPrivilege('ProjectAdd'))
  const canExportProjects = Boolean(isAdmin || hasPrivilege('ProjectExport'))

  // ── Financials Foundation ──────────────────────────────────────────────────
  const canViewFinancials = Boolean(isAdmin || hasPrivilege('ProjectFinancialsView'))
  const canManageFinancials = Boolean(isAdmin || hasPrivilege('ProjectFinancialsCreate'))
  const canExportFinancials = Boolean(isAdmin || hasPrivilege('ProjectFinancialsExport'))

  // ── Option A Hierarchical Dependents (Require canViewFinancials) ───────────
  const canViewCustomer360 = Boolean(
    canViewFinancials && (isAdmin || hasPrivilege('CustomerProfitabilityView'))
  )
  const canExportCustomer360 = Boolean(
    canViewCustomer360 && (isAdmin || hasPrivilege('CustomerProfitabilityExport'))
  )

  const canViewAmcSchedule = Boolean(
    canViewFinancials && (isAdmin || hasPrivilege('AmcBillingScheduleView'))
  )
  const canManageAmcPayments = Boolean(
    canViewAmcSchedule && (isAdmin || hasPrivilege('AmcBillingScheduleCreate'))
  )

  return {
    isAdmin,
    hasPrivilege,
    // Operations
    canViewProject,
    canEditProject,
    canCreateProject,
    canExportProjects,
    // Financials Foundation
    canViewFinancials,
    canManageFinancials,
    canExportFinancials,
    // Dependent Sub-features
    canViewCustomer360,
    canExportCustomer360,
    canViewAmcSchedule,
    canManageAmcPayments,
  }
}
